import React from 'react';
import { Camera, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { TravelProfile } from '../../../types/matching';
import { getCityPhotos } from '../../../lib/unsplash-service';

interface ProfileDetailPhotosProps {
  profile: TravelProfile;
}

export function ProfileDetailPhotos({ profile }: ProfileDetailPhotosProps) {
  const [destinationPhotos, setDestinationPhotos] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);
  const [fullscreenPhoto, setFullscreenPhoto] = React.useState<string | null>(null);

  // Fetch destination photos if no user photos
  React.useEffect(() => {
    const fetchDestinationPhotos = async () => {
      if (!profile.photos?.length && profile.preferences.city) {
        try {
          setIsLoading(true);
          const photos = await getCityPhotos(profile.preferences.city, 6);
          setDestinationPhotos(photos);
        } catch (error) {
          console.error('Error fetching destination photos:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDestinationPhotos();
  }, [profile.photos, profile.preferences.city]);

  // No photos to display
  if (!profile.photos?.length && !destinationPhotos.length && !isLoading) {
    return null;
  }

  // Limit to 6 photos
  const photos = profile.photos?.length 
    ? profile.photos.slice(0, 6) 
    : destinationPhotos.slice(0, 6);

  // Only show if we have photos
  if (photos.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {profile.photos?.length ? 'Photos' : 'Photos de la destination'}
      </h3>
      
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => (
          <div 
            key={index}
            className="aspect-square rounded-lg overflow-hidden cursor-pointer relative group"
            onClick={() => setFullscreenPhoto(photo)}
          >
            <img 
              src={photo} 
              alt={`Photo ${index + 1}`} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
          </div>
        ))}
        
        {isLoading && (
          <div className="aspect-square rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center animate-pulse">
            <Camera className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
        )}
      </div>

      {/* Fullscreen Photo View */}
      {fullscreenPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFullscreenPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenPhoto(null);
            }}
          >
            <X className="w-6 h-6" />
          </button>
          
          <img 
            src={fullscreenPhoto} 
            alt="Photo en plein écran" 
            className="max-h-[85vh] max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {photos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = photos.indexOf(fullscreenPhoto);
                  const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
                  setFullscreenPhoto(photos[prevIndex]);
                }}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = photos.indexOf(fullscreenPhoto);
                  const nextIndex = (currentIndex + 1) % photos.length;
                  setFullscreenPhoto(photos[nextIndex]);
                }}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
